// src/components/MatterView.jsx
//
// Wave 2.5 (22 Apr 2026) rewire:
//   (1) Removed inline handleUpload + onDrop (compliance bypass path)
//   (2) Removed fileRef (no longer needed)
//   (3) AwaitingPanel + DocsTab now show "Upload document" buttons
//       that open UploadModal instead of inline dropzones
//   (4) Added showUploadModal state + UploadModal render
//   (5) UploadModal's onUploaded callback triggers onMatterUpdate()
//
// Phase 2b saved-card wiring (24 Apr 2026):
//   (6) handleRun branches on response.mode from createCheckoutSession
//         - mode='checkout'   → redirect to Stripe (existing flow, unchanged)
//         - mode='saved_card' → show RunConfirmModal, await user click, then
//                               confirmRun() fires PaymentIntent + SFN
//   (7) Added confirmModalData state + RunConfirmModal render
//   (8) Added handleConfirmRun callback (calls /run/confirm, starts polling)

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  createCheckoutSession,
  confirmRun,
  getRunStatus,
  deleteRun,
  deleteMatter,
  extendMatter,
  listRuns,
  listDocuments,
  deleteDocument,
} from '../api';
import UploadModal from './UploadModal';
import { RunConfirmModal } from './Modals';
import IntakeFormModal from './IntakeFormModal';

const GOLD = '#C8A84B';
const DARK = '#0D1B2A';

const scoreColor = s => s >= 8 ? '#3A9A58' : s >= 6 ? '#B07820' : '#C04040';

// ── Run progress card (B9) ──────────────────────────────────────────────
// Keys off the runs.status column (in_progress_stageN / _assemble), the only
// per-stage signal the backend reliably writes. The runs.stage column is NULL
// in practice, so we do NOT depend on it.
const RUN_STEPS = [
  { key: 'in_progress_stage0',    label: 'Reading your documents' },
  { key: 'in_progress_stage1',    label: 'Jurisdiction & threshold' },
  { key: 'in_progress_stage2',    label: 'Classifying the issues' },
  { key: 'in_progress_stage3',    label: 'Cause-of-action elements' },
  { key: 'in_progress_stage4',    label: 'Assessing damages' },
  { key: 'in_progress_stage5',    label: 'Calculating LitFit™ score' },
  { key: 'in_progress_assemble',  label: 'Assembling your report' },
];

function RunProgress({ status }) {
  // Resolve current step from the status string. Unknown/early in_progress
  // values fall back to step 0 so the bar never renders blank.
  let idx = RUN_STEPS.findIndex(s => status === s.key);
  if (idx < 0) idx = (status || '').includes('in_progress') ? 0 : 0;
  const current = RUN_STEPS[idx];
  const total = RUN_STEPS.length;

  return (
    <div style={{
      background: '#FFF', border: `1px solid ${GOLD}`, borderRadius: 10,
      padding: '16px 20px', maxWidth: 440,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: DARK }}>
          {current.label}
        </span>
        <span style={{ fontSize: 12, fontWeight: 500, color: GOLD, letterSpacing: '0.03em' }}>
          Step {idx + 1} of {total}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 4 }}>
        {RUN_STEPS.map((s, i) => (
          <div key={s.key} style={{
            flex: 1, height: 6, borderRadius: 3,
            background: i < idx ? GOLD
                      : i === idx ? GOLD
                      : '#E8E4DC',
            opacity: i === idx ? 1 : i < idx ? 0.55 : 1,
            animation: i === idx ? 'pulse 1.2s ease-in-out infinite' : 'none',
          }} />
        ))}
      </div>
      <div style={{ fontSize: 11.5, color: '#888', marginTop: 11, lineHeight: 1.45 }}>
        Typically around 10 minutes. You can safely leave this page — your
        report will be waiting when you return.
      </div>
    </div>
  );
}

export default function MatterView({ matter, token, onMatterUpdate, onShowFeedback, expectNewRun, onRunFound }) {
  const [tab, setTab]                     = useState('summary');
  const [perspective, setPerspective]     = useState('PLAINTIFF');
  const [stage, setStage]                 = useState('Pre-litigation');
  const [opponent, setOpponent]           = useState('Well-resourced firm');
  const [settlement, setSettlement]       = useState('Open to settlement');
  const [running, setRunning]             = useState(false);
  const [runStatus, setRunStatus]         = useState('');
  const [liveStatus, setLiveStatus]       = useState(null);  // live run status from poll, drives RunProgress auto-advance
  const [crrText, setCrrText]             = useState(null);
  const [certUrl, setCertUrl]             = useState(null);
  const [crrLoading, setCrrLoading]       = useState(false);  // B9-P4 (A2): CRR fetch in flight
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState(null);  // Phase 2b: saved-card confirm
  const polledRunRef = useRef(null);  // A1: guard against double-poll
  const finishedRunRef = useRef(null); // rs1-c3: run_ids that have hit a terminal tick — never re-poll
  const loadedCrrIdRef = useRef(null); // rs1-c3: run_id whose CRR is currently in crrText — skip redundant refetch
  const [startedRunId, setStartedRunId] = useState(null);  // A1: authoritative run_id from confirm
  const [showIntakeModal, setShowIntakeModal] = useState(false);  // Path 4D-light: intake form gate
  const [showTriageModal, setShowTriageModal] = useState(false);  // readability triage gate
  const [excluding, setExcluding] = useState(false);  // triage exclude in flight
  const pollRef = useRef();
  const certRetryRef = useRef();   // A1: bounded post-complete certificate poll

  // Latest run
  // /matters returns flat run fields on the matter (no runs[] array).
  // Build a synthetic latestRun so the rest of the component works unchanged.
  const latestRun = matter.last_run_id ? {
    run_id:        matter.last_run_id,
    status:        matter.run_status,
    litfit_score:  matter.litfit_score,
    created_at:    matter.updated_at,        // matter list has no run_created_at; use matter.updated_at for display
    output_crr_full: null,                   // CRR text comes via crrText (fetched from S3), not inline
  } : null;
  // B9 (A7/A6): real runs + document records from the B8 backend endpoints.
  // null = not loaded yet (synthetic latestRun array is the fallback below).
  const [runsList, setRunsList] = useState(null);
  const [docsList, setDocsList] = useState(null);
  // B9-P3 (A7 residual + A4): which run's CRR the Overview shows; null = latest.
  const [selectedRunId, setSelectedRunId] = useState(null);
  useEffect(() => { setSelectedRunId(null); }, [matter?.matter_id]);
  useEffect(() => {
    setRunsList(null);
    setDocsList(null);
    if (!matter?.matter_id) return;
    listRuns(token, matter.matter_id)
      .then(data => setRunsList(data.runs || []))
      .catch(err => console.error('Runs list load failed:', err));
    listDocuments(token, matter.matter_id)
      .then(data => setDocsList(data.documents || []))
      .catch(err => console.error('Documents list load failed:', err));
  // refetch when a run completes (onMatterUpdate refreshes matter fields)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matter?.matter_id, matter?.last_run_id, matter?.run_status]);

  // Reports/History tabs consume this array. Prefer the fetched list;
  // the synthetic single-run array remains the fallback while loading.
  const runs = runsList ?? (latestRun ? [latestRun] : []);
  // Only complete runs have a CRR behind them (ghost awaiting_confirm and
  // failed runs do not) — Reports tab consumes this.
  const completedRuns = runs.filter(r => r.status === 'complete');
  const score = matter.litfit_score ?? null;
  // B9-P3: run being viewed on Overview (null = latest)
  const viewedRun = selectedRunId ? runs.find(r => r.run_id === selectedRunId) : null;
  const viewingEarlier = !!(viewedRun && viewedRun.run_id !== matter?.last_run_id);
  const displayScore = viewingEarlier ? (viewedRun.litfit_score ?? null) : score;

  // Load CRR for already-completed runs when matter is selected.
  // Key off the latest run's OWN status (reliable) rather than denormalized
  // matter.last_run_id / matter.run_status fields, which may not be populated.
  useEffect(() => {
    // B9-P3: load the selected run's CRR; fall back to the latest complete run.
    const targetRunId = selectedRunId ||
      ((matter?.last_run_id && matter?.run_status === 'complete') ? matter.last_run_id : null);
    // rs1-c3: if this run's CRR is already displayed (poller or prior load), keep it.
    if (targetRunId && loadedCrrIdRef.current === targetRunId && crrText) return;
    setCrrText(null);
    setCertUrl(null);
    loadedCrrIdRef.current = null;
    if (targetRunId) {
      setCrrLoading(true);
      getRunStatus(token, targetRunId)
        .then(data => { if (data.certificate_url) setCertUrl(data.certificate_url); return data; })
        .then(data => {
          if (data.crr_url) return fetch(data.crr_url).then(res => res.text());
          if (data.output_crr_full) return data.output_crr_full;
          throw new Error('No CRR data available');
        })
        .then(text => { if (text) { setCrrText(text); loadedCrrIdRef.current = targetRunId; } })
        .catch(err => console.error('CRR load failed:', err))
        .finally(() => setCrrLoading(false));
    } else {
      setCrrLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matter?.matter_id, matter?.last_run_id, matter?.run_status, selectedRunId]);

  // ── A1 / Fresh-checkout bridge ───────────────────────────────────────
  // On return from Stripe, App clears the URL (pushState '/') so urlRunId is
  // null and the webhook may not have minted the run yet. Poll listRuns until
  // an active run appears, then hand it to the unified poller via startedRunId.
  useEffect(() => {
    if (!expectNewRun || !matter?.matter_id) return;
    setRunning(true);
    setRunStatus('Payment confirmed. Starting your analysis…');
    const bridge = setInterval(() => {
      listRuns(token, matter.matter_id)
        .then(data => {
          const active = (data.runs || []).find(
            r => r.status?.startsWith('in_progress')
          );
          if (active) {
            clearInterval(bridge);
            setStartedRunId(active.run_id);
            onRunFound?.();
          }
        })
        .catch(err => console.error('Fresh-checkout bridge poll failed:', err));
    }, 2500);
    const stopAt = setTimeout(() => { clearInterval(bridge); onRunFound?.(); }, 60000);
    return () => { clearInterval(bridge); clearTimeout(stopAt); };
  }, [expectNewRun, matter?.matter_id, token, onRunFound]);
  // ── Unified polling hook ─────────────────────────────────────────────
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlRunId = urlParams.get('run_id');

    const terminalOrInactive = ['complete', 'failed', 'cancelled', 'archived', 'awaiting_confirm'];
    const dbRunActive = latestRun && !terminalOrInactive.some(
      s => latestRun.status === s || latestRun.status?.includes(s)
    );
    const activeRunId = startedRunId || urlRunId || (dbRunActive ? latestRun.run_id : null);

    if (!activeRunId) return;
    if (finishedRunRef.current === activeRunId) return; // rs1-c3: dead runs stay dead
    if (polledRunRef.current === activeRunId) return;
    polledRunRef.current = activeRunId;

    setRunStatus('Analysing matter…');
    // A1b: only seed Step 1 on a genuinely fresh start; do NOT clobber an already-advanced
    // bar when this effect re-runs on a latestRun.status change mid-run (was resetting to Step 1).
    setLiveStatus(prev => (prev && prev.includes('in_progress')) ? prev : 'in_progress_stage0');

    if (urlRunId) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    const tick = async () => {
      try {
        const res = await getRunStatus(token, activeRunId);
        if (res.certificate_url) setCertUrl(res.certificate_url);
        const stageLabels = {
          jurisdiction: 'Jurisdiction & threshold…',
          taxonomy:     'Taxonomy classification…',
          elements:     'Cause of action elements…',
          damages:      'Damages assessment…',
          litfit:       'Calculating LitFit™ score…',
          assemble:     'Assembling report…',
        };
        if (res.status === 'running' || res.status?.includes('in_progress')) {
          setRunning(true);  // card renders on evidence of a live run, not mount-time guess
          setRunStatus(stageLabels[res.stage] || 'Analysing…');
          setLiveStatus(res.status);  // advance the progress bar to the live stage
        } else if (res.status === 'complete') {
          finishedRunRef.current = activeRunId;  // rs1-c3
          clearInterval(pollRef.current);
          setRunning(false);
          setRunStatus('');
          setStartedRunId(null);
          polledRunRef.current = null;
          if (res.crr_url) {
            try { setCrrText(await (await fetch(res.crr_url)).text()); loadedCrrIdRef.current = activeRunId; } catch { /* fallback */ }
          }
          // A1 fix: the compliance certificate is generated ~1-3s AFTER the run
          // reaches 'complete' (cleanup-s3 fires on the SUCCEEDED event), so the
          // tick that sees 'complete' almost never carries certificate_url.
          // Bounded retry: re-check every 3s, max 10 attempts, then give up
          // (the mount-time effect / F5 remains the fallback).
          if (res.certificate_url) {
            setCertUrl(res.certificate_url);
          } else {
            let attempts = 0;
            clearInterval(certRetryRef.current);
            certRetryRef.current = setInterval(async () => {
              attempts += 1;
              try {
                const r = await getRunStatus(token, activeRunId);
                if (r.certificate_url) {
                  setCertUrl(r.certificate_url);
                  clearInterval(certRetryRef.current);
                  return;
                }
              } catch (err) {
                console.error('Certificate poll error:', err);
              }
              if (attempts >= 10) clearInterval(certRetryRef.current);
            }, 3000);
          }
          setLiveStatus('complete');
          onMatterUpdate();
          // Feedback modal deactivated — run completes straight to overview/score.
          // (onShowFeedback is no longer called; re-enable here if reinstated.)
        } else if (res.is_terminal || res.status === 'cancelled' ||
                   res.status?.includes('failed')) {
          finishedRunRef.current = activeRunId;  // rs1-c3
          // rs1 companion: server-computed is_terminal is authoritative;
          // status checks retained as belt-and-braces for cached responses.
          clearInterval(pollRef.current);
          setRunning(false);
          setRunStatus('');
          setLiveStatus(null);
          setStartedRunId(null);
          polledRunRef.current = null;
          alert(`This run did not complete: ${res.error_detail || 'unknown error'}. You have not been charged for an incomplete run. You can start a new run.`);
          onMatterUpdate();
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    };
    tick();  // immediate first tick — no 4s window of guessed state
    pollRef.current = setInterval(tick, 4000);

    return () => { clearInterval(pollRef.current); clearInterval(certRetryRef.current); polledRunRef.current = null; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matter?.matter_id, latestRun?.run_id, latestRun?.status, startedRunId]);

  // Cleanup polling on unmount
  useEffect(() => () => { clearInterval(pollRef.current); clearInterval(certRetryRef.current); }, []);

  // ── Run ───────────────────────────────────────────────────────────────
  // Phase 2b: branch on response.mode
  //   'checkout'   → fresh card flow; redirect to Stripe
  //   'saved_card' → show RunConfirmModal; confirmation triggers confirmRun
  const handleRun = useCallback(() => {
    if (!matter.matter_s3_key) {
      alert('Please upload a matter brief first.');
      return;
    }
    // Readability triage gate: if any uploaded document could not be read
    // (is_readable === false), halt and let the lawyer exclude or fix them
    // before paying. null (unchecked / image exhibit) never blocks. The
    // create-checkout backend gate is the hard backstop regardless.
   // Don't let the gate fail open while docs are still loading: if we can't
    // see the readability flags yet, ask the user to retry rather than firing
    // a run the backend will reject with a raw alert.
    if (docsList === null) {
      alert('Still loading your documents — give it a second and click Run again.');
      return;
    }
    if (docsList.some(d => d.is_readable === false)) {
      setShowTriageModal(true);
      return;
    }
    // Path 4D-light: open intake modal first. Payment flow continues
    // inside handleIntakeSubmit once the lawyer has provided strategy context.
    setShowIntakeModal(true);
  }, [matter, docsList]);

  // Remove a single document from the matter (soft-delete; the file is kept in
  // S3 and can be re-uploaded). Works on any doc regardless of readability.
  const handleRemoveDoc = useCallback(async (doc) => {
    const msg = doc.is_readable === false
      ? `Remove "${doc.original_filename}"?\n\nThis document couldn’t be read (a scanned image with no text), so it isn’t contributing to the analysis. To include it, you’ll need a text-based or OCR’d version: remove this one, then upload the readable version.`
      : `Remove "${doc.original_filename}"?\n\nIt won’t be included in the analysis. You can upload it again if you wish to include it later (or if you remove it by mistake).`;
    if (!window.confirm(msg)) {
      return;
    }
    try {
      await deleteDocument(token, matter.matter_id, doc.doc_id);
      const data = await listDocuments(token, matter.matter_id);
      setDocsList(data.documents || []);
    } catch (err) {
      alert('Could not remove the document: ' + (err?.message || err));
    }
  }, [token, matter]);

 const handleIntakeSubmit = useCallback(async (intakeData) => {
    setShowIntakeModal(false);
    setPerspective(intakeData.perspective);
    // NB: do NOT set running/liveStatus here — the progress bar must not appear
    // until the run actually fires (saved-card path waits for confirm modal;
    // checkout path redirects to Stripe). Premature set caused the bar to flash
    // before the confirmation window.
    try {
      // intakeData carries perspective + run_context fields.
      const response = await createCheckoutSession(
        token,
        matter.matter_id,
        intakeData.perspective,
        intakeData,
      );

      // Fresh checkout path — redirect to Stripe
      if (response?.mode === 'checkout' && response.checkout_url) {
        window.location.href = response.checkout_url;
        return;
      }

      // Saved-card path — show confirm modal; defer run start until user confirms
      if (response?.mode === 'saved_card' && response.run_id) {
        setConfirmModalData({
          run_id:     response.run_id,
          perspective: intakeData.perspective,
          card_brand: response.card_brand,
          card_last4: response.card_last4,
        });
        setRunning(false);
        setRunStatus('');
        return;
      }

      // Unexpected response — don't silently fail
      console.error('Unexpected createCheckoutSession response:', response);
      setRunning(false);
      setRunStatus('');
      alert('Unexpected response starting run. Please try again or contact support.');
    } catch (e) {
      setRunning(false);
      setRunStatus('');
      alert(`Failed to start run: ${e.message}`);
    }
  }, [token, matter]);  // perspective now sourced from intakeData

  // ── Confirm run via saved card (Phase 2b) ─────────────────────────────
  // User has clicked "Confirm — Run LitFit" in the RunConfirmModal. Fire the
  // /run/confirm endpoint which triggers the off-session PaymentIntent and
  // starts the Step Functions execution. The polling useEffect above will
  // pick up the now-active run once onMatterUpdate refreshes matter state.
  const handleConfirmRun = useCallback(async (attestation) => {
    if (!confirmModalData?.run_id) return;
    const runId = confirmModalData.run_id;
    setConfirmModalData(null);
    setStartedRunId(runId);  // A1: hand authoritative run_id to poll
    setRunning(true);
    setRunStatus('Starting analysis…');
    setLiveStatus('in_progress_stage0');  // show Step 1 the moment the run fires
    try {
      await confirmRun(token, runId, attestation);
      onMatterUpdate();  // Sidebar refresh; polling useEffect picks up active run
    } catch (e) {
      setRunning(false);
      setRunStatus('');
      alert(`Failed to confirm run: ${e.message}`);
      onMatterUpdate();  // Refresh anyway — backend may have marked the run failed
    }
  }, [token, confirmModalData, onMatterUpdate]);

  // ── Upload callback — refresh matter after successful upload ──────────
  const handleUploaded = useCallback(async () => {
    onMatterUpdate(); // keep parent/sidebar in sync
    // Force local docs re-fetch so the run-gate sees current readability flags.
    // Upload mutates matter_documents but none of the docs-effect deps, so the
    // effect never re-fires on its own — without this, the gate runs on stale
    // docsList and the backend backstop catches unreadable docs with a raw alert.
    try {
      const data = await listDocuments(token, matter.matter_id);
      setDocsList(data.documents || []);
    } catch (err) {
      console.error('Failed to refresh docs after upload:', err);
    }
  }, [onMatterUpdate, token, matter.matter_id]);

  // ── Delete ────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!window.confirm(`Delete the entire matter "${matter.matter_name}" and ALL its runs and uploaded documents? This cannot be undone.`)) return;
    await deleteMatter(token, matter.matter_id);
    onMatterUpdate(null, true);
  };

  const handleExtend = async () => {
    const days = matter.retention_days || 30;
    try {
      await extendMatter(token, matter.matter_id);
      onMatterUpdate();
      alert(`Matter kept active — retention extended by ${days} days from today.`);
    } catch (e) {
      alert(`Failed to extend retention: ${e.message}`);
    }
  };

  // ── Score / status display ────────────────────────────────────────────
  const statusLabel = matter.status === 'archived' ? 'Archived'
    : !matter.matter_s3_key ? 'Awaiting Brief'
    : score >= 8 ? 'Trial Ready'
    : score >= 6 ? 'Needs Evidence'
    : score != null ? 'Material Weaknesses'
    : 'Ready to Analyse';

  const statusStyle = matter.status === 'archived'
    ? { background: 'rgba(180,180,180,0.12)', color: '#888' }
    : !matter.matter_s3_key
    ? { background: 'rgba(180,180,180,0.12)', color: '#888' }
    : score >= 8 ? { background: 'rgba(64,160,96,0.1)', color: '#2D8A4E' }
    : score >= 6 ? { background: 'rgba(200,140,50,0.1)', color: '#9A6010' }
    : score != null ? { background: 'rgba(200,80,80,0.08)', color: '#AA3333' }
    : { background: 'rgba(200,168,75,0.1)', color: '#9A6010' };

  return (
    <div style={{ display: 'flex', flex: 1, flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>

      {/* Header */}
      <div style={{
        background: '#FFF', borderBottom: '1px solid #E8E4DC',
        padding: '22px 32px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
      }}>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 22, fontWeight: 500, color: DARK, flex: 1, minWidth: 200 }}>
          {matter.matter_name}
        </div>

        <span style={{ fontSize: 11.5, fontWeight: 500, padding: '4px 12px', borderRadius: 20, letterSpacing: '0.04em', ...statusStyle }}>
          {statusLabel}
        </span>

        {score != null && (
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 38, fontWeight: 500, color: scoreColor(score), lineHeight: 1 }}>
              {score.toFixed(1)}
            </div>
            <div style={{ fontSize: 10, color: '#BBB', marginTop: 2 }}>LitFit™ / 10</div>
          </div>
        )}

        {matter.status !== 'archived' && (
          running ? (
            <RunProgress status={liveStatus || latestRun?.status} />
          ) : (
            matter.matter_s3_key && (
              <button onClick={handleRun} style={{
                background: DARK, color: '#FFF', border: 'none',
                padding: '10px 18px', borderRadius: 8, fontSize: 13,
                fontFamily: "'DM Sans', sans-serif", cursor: 'pointer',
                letterSpacing: '0.02em', transition: 'background 0.15s',
              }}>
                {score != null ? 'Re-analyse →' : 'Run LitFit™ →'}
              </button>
            )
          )
        )}

        <ActionMenu matter={matter} onExtend={handleExtend} onDelete={handleDelete} />
      </div>

      {/* Matter pills (A11: orphaned summary textarea removed 12 Jun — the
          matter brief is captured at the run step via IntakeFormModal) */}
      {latestRun && (
      <div style={{ margin: '20px 32px 0', background: '#FFF', border: '1px solid #E8E4DC', borderRadius: 10, padding: '18px 22px' }}>
        {latestRun && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 12, paddingTop: 12, borderTop: '1px solid #F0EDE7' }}>
            <Pill color={score >= 8 ? 'green' : score >= 6 ? 'amber' : 'red'} text={`LitFit™ ${score?.toFixed(1)}`} />
            <Pill color="amber" text={`Last run ${new Date((runsList && runs[0]?.created_at) || latestRun.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}`} />
          </div>
        )}
      </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', padding: '0 32px', borderBottom: '1px solid #E8E4DC', background: '#FFF', marginTop: 20 }}>
        {['summary', 'reports', 'docs', 'history'].map(t => (
          <div
            key={t}
            onClick={() => setTab(t)}
            style={{
              fontSize: 13, color: tab === t ? DARK : '#BBB',
              fontWeight: tab === t ? 500 : 400,
              padding: '11px 18px', cursor: 'pointer',
              borderBottom: tab === t ? `2px solid ${GOLD}` : '2px solid transparent',
              transition: 'all 0.15s', userSelect: 'none', whiteSpace: 'nowrap',
            }}
          >
            {{ summary: 'Overview', reports: 'Reports', docs: 'Uploaded Documents', history: 'Run History' }[t]}
          </div>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ padding: '24px 32px', flex: 1, overflowY: 'auto', background: '#F7F5F0' }}>

        {tab === 'summary' && (
          (crrText || latestRun?.output_crr_full) ? (
            <>
              {viewingEarlier && (
                <div style={{ background: '#FBF7EC', border: '1px solid #E5D9B8', borderRadius: 8, padding: '10px 16px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12, fontSize: 12.5, color: '#7A6A3A' }}>
                  <span>
                    Viewing earlier report — {new Date(viewedRun.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })} · LitFit™ {viewedRun.litfit_score?.toFixed(1) ?? '—'}
                  </span>
                  <button onClick={() => setSelectedRunId(null)} style={{ marginLeft: 'auto', fontSize: 12, padding: '4px 11px', border: '1px solid #D8C98F', background: '#FFF', borderRadius: 6, cursor: 'pointer', color: '#7A6A3A', fontFamily: "'DM Sans',sans-serif", whiteSpace: 'nowrap' }}>
                    Back to latest →
                  </button>
                </div>
              )}
              <CRRSummary crr={crrText || latestRun.output_crr_full} score={displayScore} certUrl={certUrl} />
            </>
          ) : crrLoading ? (
            <Empty text="Loading report…" />
          ) : (latestRun && latestRun.litfit_score != null) ? (
            <PurgedNotice score={score} />
          ) : (
            <AwaitingPanel
              matter={matter}
              onOpenUpload={() => setShowUploadModal(true)}
              running={running}
            />
          )
        )}

        {tab === 'reports' && (
          completedRuns.length === 0 ? (
            <Empty text="No reports generated yet" />
          ) : (
            <>
              <div style={{ fontSize: 12, color: '#BBB', marginBottom: 16 }}>
                Case Readiness Reports — newest first. Click to view or delete.
              </div>
              {completedRuns.map(r => <ReportItem key={r.run_id} run={r} token={token} onView={() => { setSelectedRunId(r.run_id); setTab('summary'); }} onDelete={() => {
                if (!window.confirm('Delete this run? Your matter and uploaded documents will be preserved.')) return;
                deleteRun(token, r.run_id).then(onMatterUpdate);
              }} />)}
            </>
          )
        )}

        {tab === 'docs' && (
          <DocsTab
            matter={matter}
            docs={docsList}
            onOpenUpload={() => setShowUploadModal(true)}
            onRemoveDoc={handleRemoveDoc}
            running={running}
          />
        )}

        {tab === 'history' && (
          <HistoryTab
            runs={runs}
            perspective={perspective} setPerspective={setPerspective}
            stage={stage} setStage={setStage}
            opponent={opponent} setOpponent={setOpponent}
            settlement={settlement} setSettlement={setSettlement}
            onRun={handleRun} running={running}
            onViewRun={(id) => { setSelectedRunId(id); setTab('summary'); }}
          />
        )}
      </div>

      {/* Upload modal — opens from AwaitingPanel or DocsTab button */}
      {showUploadModal && (
        <UploadModal
          matter={matter}
          token={token}
          onClose={() => setShowUploadModal(false)}
          onUploaded={handleUploaded}
        />
      )}

      {/* Phase 2b — saved-card run confirmation modal */}
      {confirmModalData && (
        <RunConfirmModal
          matter={matter}
          perspective={confirmModalData.perspective}
          onConfirm={handleConfirmRun}
          onClose={() => setConfirmModalData(null)}
        />
      )}

      {/* Path 4D-light — intake form modal */}
      {showIntakeModal && (
        <IntakeFormModal
          matter={matter}
          initialPerspective={perspective}
          onClose={() => setShowIntakeModal(false)}
          onSubmit={handleIntakeSubmit}
        />
      )}

      {showTriageModal && (() => {
        const unreadable = (docsList || []).filter(d => d.is_readable === false);
        const onExclude = async () => {
          setExcluding(true);
          try {
            for (const d of unreadable) {
              await deleteDocument(token, matter.matter_id, d.doc_id);
            }
            const data = await listDocuments(token, matter.matter_id);
            setDocsList(data.documents || []);
            setShowTriageModal(false);
          } catch (err) {
            alert('Could not exclude one or more documents: ' + (err?.message || err));
          } finally {
            setExcluding(false);
          }
        };
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(13,27,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: '#FFF', borderRadius: 12, padding: '28px 30px', width: 'min(560px, 92vw)', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 18px 50px rgba(0,0,0,0.3)' }}>
              <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 20, color: '#0D1B2A', marginBottom: 10 }}>
                Some documents couldn’t be read
              </div>
              <div style={{ fontSize: 13.5, color: '#555', lineHeight: 1.6, marginBottom: 16 }}>
                {unreadable.length === 1 ? 'This document appears to be a scanned image' : 'These documents appear to be scanned images'} with no readable text. {unreadable.length === 1 ? 'It' : 'They'} cannot be analysed and would not contribute to your report. You can exclude {unreadable.length === 1 ? 'it' : 'them'} from this analysis (the file stays in your library, so you can replace {unreadable.length === 1 ? 'it' : 'them'} with a text or OCR’d version later), or go back and fix {unreadable.length === 1 ? 'it' : 'them'} first.
              </div>
              <div style={{ background: '#FBF8F2', border: '1px solid #EDE6D8', borderRadius: 8, padding: '12px 14px', marginBottom: 18 }}>
                {unreadable.map((d, i) => (
                  <div key={d.doc_id || i} style={{ fontSize: 12.5, color: '#7A4F12', display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
                    <span style={{ flexShrink: 0 }}>⚠</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.original_filename}</span>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button onClick={() => setShowTriageModal(false)} disabled={excluding}
                  style={{ background: 'transparent', color: '#555', border: '1px solid #D8D4CC', padding: '9px 16px', borderRadius: 8, fontSize: 13, fontFamily: "'DM Sans', sans-serif", cursor: excluding ? 'default' : 'pointer' }}>
                  Go back & fix
                </button>
                <button onClick={onExclude} disabled={excluding}
                  style={{ background: '#0D1B2A', color: '#FFF', border: 'none', padding: '9px 16px', borderRadius: 8, fontSize: 13, fontFamily: "'DM Sans', sans-serif", cursor: excluding ? 'default' : 'pointer', opacity: excluding ? 0.6 : 1 }}>
                  {excluding ? 'Excluding…' : (unreadable.length === 1 ? 'Exclude it & review' : 'Exclude them & review')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.25} }`}</style>
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────

function Pill({ color, text }) {
  const colors = { green: '#40A060', amber: '#D09030', red: '#D95050' };
  return (
    <span style={{ fontSize: 12, color: '#888', display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: colors[color], flexShrink: 0 }} />
      {text}
    </span>
  );
}

function ActionMenu({ matter, onExtend, onDelete }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setOpen(!open)} style={{
        background: '#F7F5F0', border: '1px solid #C8B89A',
        borderRadius: 6, padding: '6px 14px', cursor: 'pointer', color: '#3A3A3A',
        fontFamily: "'DM Sans', sans-serif", fontSize: 18, fontWeight: 700, lineHeight: 1,
      }}>⋯</button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: '100%', marginTop: 4,
          background: '#FFF', border: '1px solid #E8E4DC', borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,0.08)', zIndex: 100, minWidth: 160,
        }}>
          <MenuItem label={`Keep active — extend ${matter.retention_days || 30} days`} onClick={() => { setOpen(false); onExtend(); }} />
          <MenuItem label="Delete entire matter" onClick={() => { setOpen(false); onDelete(); }} danger />
        </div>
      )}
    </div>
  );
}

function MenuItem({ label, onClick, danger }) {
  return (
    <div onClick={onClick} style={{
      padding: '10px 16px', fontSize: 13, cursor: 'pointer',
      color: danger ? '#D95050' : '#333',
      borderBottom: '1px solid #F5F2EE',
    }}>{label}</div>
  );
}

function Empty({ text }) {
  return <div style={{ textAlign: 'center', padding: '50px 0', color: '#CCC', fontSize: 13 }}>{text}</div>;
}

function PurgedNotice({ score }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 24px', color: '#666' }}>
      <div style={{ fontSize: 28, marginBottom: 12 }}>🔒</div>
      <div style={{ fontSize: 15, fontWeight: 600, color: '#333', marginBottom: 8 }}>
        Report no longer available
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.5, maxWidth: 440, margin: '0 auto' }}>
        This Case Readiness Report{score != null ? ` (LitFit™ ${score.toFixed(1)})` : ''} was
        automatically deleted 30 days after it was generated, in line with our retention policy.
        The matter material and report are no longer held on our servers. If you kept your own
        copy you still have it; otherwise you can run a fresh report on this matter.
      </div>
    </div>
  );
}

function CRRSummary({ crr, score, certUrl }) {
  return (
    <>
      {certUrl && (
        <div style={{ marginBottom: 14 }}>
          <a href={certUrl} target="_blank" rel="noopener noreferrer"
            style={{
              display: 'inline-block', fontSize: 12.5, fontWeight: 500,
              color: '#9a7b1f', textDecoration: 'none',
              border: '1px solid #E0DDD6', borderRadius: 7, padding: '8px 14px',
              background: '#FFF',
            }}
            title="Open the Compliance Certificate for this run (SC Gen 23 / GPN-AI audit record)">
            Download Compliance Certificate →
          </a>
        </div>
      )}
      <div style={{ marginBottom: 14 }}>
        <button onClick={() => window.print()} className="noprint"
          style={{
            display: 'inline-block', fontSize: 12.5, fontWeight: 500,
            color: '#9a7b1f', textDecoration: 'none', cursor: 'pointer',
            border: '1px solid #E0DDD6', borderRadius: 7, padding: '8px 14px',
            background: '#FFF', fontFamily: "'DM Sans', sans-serif",
          }}
          title="Save this Case Readiness Report as a PDF (use your browser's Save as PDF option)">
          Save report as PDF →
        </button>
      </div>
      {score != null && (
        <div style={{
          background: '#FFF', border: '1px solid #E8E4DC', borderRadius: 10,
          padding: '20px 24px', marginBottom: 14,
        }}>
          <div style={{ fontSize: 10, fontWeight: 500, color: '#BBB', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 10 }}>
            LitFit™ Assessment
          </div>
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 17, fontWeight: 500, color: '#0D1B2A', marginBottom: 9 }}>
            {score.toFixed(1)} / 10
          </div>
        </div>
      )}
      <div className="crr-printable" style={{ background: '#FFF', border: '1px solid #E8E4DC', borderRadius: 10, padding: '20px 24px' }}>
        <div style={{ fontSize: 10, fontWeight: 500, color: '#BBB', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 10 }}>
          Case Readiness Report
        </div>
        <pre style={{ fontSize: 12.5, lineHeight: 1.7, color: '#444', fontWeight: 300, fontFamily: "'DM Sans', sans-serif", whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {crr}
        </pre>
      </div>
    </>
  );
}

function ReportItem({ run, token, onView, onDelete }) {
  const score = run.litfit_score;
  const c = score ? scoreColor(score) : '#888';
  const date = new Date(run.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  return (
    <div onClick={onView} style={{
      background: '#FFF', border: '1px solid #E8E4DC', borderRadius: 8,
      padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14,
      marginBottom: 10, transition: 'border-color 0.15s', cursor: 'pointer',
    }}>
      <div style={{
        width: 38, height: 46, background: '#F7F5F0', border: '1px solid #E8E4DC',
        borderRadius: 5, display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: 3, flexShrink: 0,
      }}>
        <span style={{ fontSize: 9, fontWeight: 600, color: '#B03030' }}>CRR</span>
        {score && <span style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 14, fontWeight: 500, color: c }}>{score.toFixed(1)}</span>}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 400, color: '#222', marginBottom: 3 }}>
          {run.perspective ? `${run.perspective} perspective · ` : ''}{date}
        </div>
        <div style={{ fontSize: 11.5, color: '#AAA' }}>
          LitFit™ {score?.toFixed(1) ?? '—'} · Status: {run.status}
          {run.source_brief_deleted && <span style={{ color: '#D95050', marginLeft: 8 }}>⚠ Source brief deleted</span>}
        </div>
      </div>
      {run.has_certificate && (
        <IconBtn label="Certificate" onClick={async () => {
          try {
            const s = await getRunStatus(token, run.run_id);
            if (s.certificate_url) window.open(s.certificate_url, '_blank', 'noopener');
            else alert('Certificate not available yet — please try again shortly.');
          } catch (e) {
            alert(`Certificate load failed: ${e.message}`);
          }
        }} />
      )}
      <IconBtn label="Delete" onClick={onDelete} />
    </div>
  );
}

function IconBtn({ label, onClick }) {
  return (
    <button onClick={(e) => { e.stopPropagation(); onClick(); }} style={{
      fontSize: 12, padding: '6px 13px',
      border: '1px solid #E0DDD6', background: '#FAFAF8',
      borderRadius: 6, cursor: 'pointer', color: '#555',
      fontFamily: "'DM Sans', sans-serif", transition: 'border-color 0.15s',
      whiteSpace: 'nowrap',
    }}>{label}</button>
  );
}

// ── AwaitingPanel (rewired) ──────────────────────────────────────────────
// No more inline dropzone. Single "Upload document" button opens UploadModal.
function AwaitingPanel({ matter, onOpenUpload, running }) {
  return (
    <div style={{ background: '#FFF', border: '1px solid #E8E4DC', borderRadius: 10, padding: '28px 32px' }}>
      <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 18, fontWeight: 400, color: '#0D1B2A', marginBottom: 10 }}>
        {matter.matter_s3_key ? 'Ready to run' : 'Awaiting documents and matter brief'}
      </div>
      <div style={{ fontSize: 13, color: '#999', fontWeight: 300, lineHeight: 1.6, marginBottom: 20 }}>
        {matter.matter_s3_key
          ? 'Documents uploaded. Add more below, or click Run LitFit™ — you’ll provide the matter brief (a factual summary of the dispute) at the run step, then your Case Readiness Report is generated.'
          : 'Upload your documents to run a LitFit™ analysis — facts, pleadings, evidence summaries, and submissions, depending on the stage of the dispute. You’ll provide the matter brief — a factual summary of the dispute — at the run step.'}
      </div>
      <button
        onClick={onOpenUpload}
        disabled={running}
        style={{
          background: running ? '#EEE' : (matter.matter_s3_key ? 'transparent' : '#0D1B2A'),
          color: running ? '#AAA' : (matter.matter_s3_key ? '#555' : '#FFF'),
          border: matter.matter_s3_key ? '1px solid #D8D4CC' : 'none',
          padding: '10px 18px', borderRadius: 8, fontSize: 13,
          fontFamily: "'DM Sans', sans-serif", cursor: running ? 'default' : 'pointer',
          letterSpacing: '0.02em',
        }}
      >
        {running ? 'Analysis in progress…' : (matter.matter_s3_key ? 'Upload more documents →' : 'Upload documents →')}
      </button>
    </div>
  );
}

// ── DocsTab (rewired) ────────────────────────────────────────────────────
// No more inline dropzone. "+ Upload" button opens UploadModal.
function DocsTab({ matter, docs, onOpenUpload, onRemoveDoc, running }) {
  // Readability badge for a document (is_readable: true | false | null).
  // null = not checked / non-PDF (e.g. image exhibit) — neutral, never alarming.
  const readabilityBadge = (isReadable) => {
    if (isReadable === true)  return { label: 'Readable', bg: 'rgba(64,160,96,0.1)', color: '#2D8A4E', title: 'Text detected — ready for analysis' };
    if (isReadable === false) return { label: 'Couldn’t read', bg: 'rgba(176,120,32,0.12)', color: '#B07820', title: 'No readable text (scanned image). Upload a text or OCR’d version.' };
    return { label: '—', bg: '#F0EDE7', color: '#AAA', title: 'Not checked (e.g. image exhibit or non-PDF)' };
  };
  const readabilityRoster = (rows) => ({
    ready:      rows.filter(d => d.is_readable === true).length,
    unreadable: rows.filter(d => d.is_readable === false).length,
    total:      rows.length,
  });

  const typeBadge = (ft) => {
    if (!ft) return 'DOC';
    if (ft.includes('pdf')) return 'PDF';
    if (ft.includes('word') || ft.includes('officedocument')) return 'DOCX';
    return 'DOC';
  };
  const hasRows = Array.isArray(docs) && docs.length > 0;
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: '#BBB' }}>
          {hasRows
            ? `${docs.length} document record${docs.length === 1 ? '' : 's'}`
            : 'No documents yet'}
          {hasRows && (() => {
            const r = readabilityRoster(docs);
            if (r.unreadable === 0) return null;
            return <span style={{ marginLeft: 10, fontSize: 11.5, color: '#B07820', fontWeight: 500 }}>
              · {r.unreadable} need{r.unreadable === 1 ? 's' : ''} attention (unreadable scan)
            </span>;
          })()}
        </div>
        <button
          onClick={onOpenUpload}
          disabled={running}
          style={{ background: running ? '#EEE' : '#0D1B2A', color: running ? '#AAA' : '#FFF', border: 'none', padding: '7px 14px', borderRadius: 8, fontSize: 12, fontFamily: "'DM Sans',sans-serif", cursor: running ? 'default' : 'pointer' }}
        >{running ? 'Analysis running…' : '+ Upload'}</button>
      </div>
      {hasRows ? (
        <>
          {docs.map((d, i) => (
            <div key={`${d.original_filename}-${i}`} style={{ background: '#FFF', border: '1px solid #E8E4DC', borderRadius: 8, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 13, marginBottom: 8 }}>
              <div style={{ width: 34, height: 34, background: '#F0EDE7', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600, color: '#B03030', flexShrink: 0 }}>
                {typeBadge(d.file_type)}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 400, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.original_filename}</div>
                <div style={{ fontSize: 11, color: '#BBB', marginTop: 2 }}>{d.doc_category || 'Uncategorised'}</div>
              </div>
              <div style={{ marginLeft: 'auto', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                {(() => { const b = readabilityBadge(d.is_readable); return (
                  <span title={b.title} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: b.bg, color: b.color, whiteSpace: 'nowrap' }}>{b.label}</span>
                ); })()}
                {onRemoveDoc && (
                  <button onClick={() => onRemoveDoc(d)} disabled={running} title={running ? 'Cannot remove documents while an analysis is running' : 'Remove this document from the matter'}
                    style={{ background: 'transparent', border: 'none', color: running ? '#D5D0C8' : '#B0AAA0', fontSize: 12, cursor: running ? 'default' : 'pointer', fontFamily: "'DM Sans', sans-serif", padding: '2px 4px', whiteSpace: 'nowrap' }}>
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))}
          <div style={{ fontSize: 11.5, color: '#AAA', lineHeight: 1.6, marginTop: 14 }}>
            These are document records, not the files themselves — uploaded documents
            are erased after each analysis completes. Keep your own copies in your
            matter file.
          </div>
        </>
      ) : null}
    </>
  );
}

function HistoryTab({ runs, perspective, setPerspective, stage, setStage, opponent, setOpponent, settlement, setSettlement, onRun, running, onViewRun }) {
  return (
    <>
      <div style={{ background: '#FFF', border: '1px solid #E8E4DC', borderRadius: 10, padding: '18px 22px', marginBottom: 20 }}>
        <div style={{ fontSize: 10, fontWeight: 500, color: '#BBB', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 13 }}>
          Strategy context — next run
        </div>
        {[
          { label: 'Perspective', value: perspective, set: setPerspective, opts: ['PLAINTIFF', 'DEFENDANT'] },
          { label: 'Stage', value: stage, set: setStage, opts: ['Pre-litigation', 'Pleadings', 'Discovery', 'Pre-trial', 'Trial'] },
          { label: 'Opponent', value: opponent, set: setOpponent, opts: ['Well-resourced firm', 'Mid-tier firm', 'Self-represented'] },
          { label: 'Settlement approach', value: settlement, set: setSettlement, opts: ['Open to settlement', 'Trial preferred', 'Commercial resolution'] },
        ].map(({ label, value, set, opts }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: '#AAA', minWidth: 120 }}>{label}</span>
            <select
              value={value} onChange={e => set(e.target.value)}
              style={{ flex: 1, fontSize: 12.5, fontFamily: "'DM Sans',sans-serif", border: '1px solid #E0DDD6', borderRadius: 6, padding: '5px 10px', background: '#FAFAF8', color: '#333', outline: 'none' }}
            >
              {opts.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
        ))}
        <div style={{ textAlign: 'right', marginTop: 8 }}>
          <button onClick={onRun} disabled={running} style={{
            background: running ? '#AAA' : '#0D1B2A', color: '#FFF', border: 'none',
            padding: '10px 18px', borderRadius: 8, fontSize: 13,
            fontFamily: "'DM Sans',sans-serif", cursor: running ? 'default' : 'pointer',
          }}>
            {running ? 'Running…' : 'Re-analyse with updated pleadings, evidence, submissions or strategy context →'}
          </button>
        </div>
      </div>

      {runs.length === 0 ? (
        <Empty text="No previous runs" />
      ) : (
        <>
          <div style={{ fontSize: 12, color: '#BBB', marginBottom: 14 }}>Previous runs — newest first</div>
          {runs.map(r => {
            const s = r.litfit_score;
            const c = s ? scoreColor(s) : '#888';
            const ghost = r.status === 'awaiting_confirm';
            const date = new Date(r.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
            return (
              <div key={r.run_id} style={{
                background: '#FFF', border: '1px solid #E8E4DC', borderRadius: 8,
                padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 16,
                marginBottom: 10, cursor: ghost ? 'default' : 'pointer',
                transition: 'border-color 0.15s', opacity: ghost ? 0.55 : 1,
              }}>
                <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 26, fontWeight: 500, color: c, minWidth: 42, textAlign: 'center' }}>
                  {s?.toFixed(1) ?? '—'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: '#333' }}>{date}</div>
                  <div style={{ fontSize: 12, color: '#BBB', marginTop: 3 }}>
                    {r.perspective ? `${r.perspective} perspective · ` : ''}
                    {ghost ? 'Payment not completed — no report generated' : r.status}
                  </div>
                </div>
                {!ghost && <button onClick={() => onViewRun(r.run_id)} style={{
                  fontSize: 12, padding: '6px 13px', border: '1px solid #E0DDD6',
                  background: '#FAFAF8', borderRadius: 6, cursor: 'pointer', color: '#555',
                  fontFamily: "'DM Sans',sans-serif",
                }}>View CRR</button>}
              </div>
            );
          })}
        </>
      )}
    </>
  );
}
